import { 
  onAuthStateChanged as firebaseOnAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  signOut as firebaseSignOut, 
  type User 
} from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db, getAuthInstance, functions } from '../firebaseClient';
import { UserProfile, Organization } from '../types';

export type AuthResult = { 
  success: true; 
  isNew?: boolean; 
  user?: User; 
  profile?: UserProfile 
} | { 
  success: false; 
  code?: string; 
  message?: string 
};

export type SignInResult = AuthResult;

// --- Utilidades de Caché de Sesión ---
export function hydrateProfileApps(profile: UserProfile): UserProfile {
  if (!profile) return profile;
  if (!Array.isArray(profile.apps) || profile.apps.length === 0) {
    const roleStr = String(profile.role || '').toLowerCase();
    if (roleStr === 'owner' || roleStr === 'admin' || roleStr === 'office') {
      profile.apps = ['attendance', 'expenses', 'inventory', 'presupuestos'];
    } else if (roleStr === 'auxiliary') {
      profile.apps = ['inventory'];
    } else {
      profile.apps = ['attendance', 'inventory'];
    }
  }
  return profile;
}

export function getCachedUserProfile(): UserProfile | null {
  const cached = localStorage.getItem('archicore_cached_profile');
  if (cached) {
    try {
      const parsed = JSON.parse(cached) as UserProfile;
      return hydrateProfileApps(parsed);
    } catch {
      return null;
    }
  }
  return null;
}

export function cacheUserProfile(profile: UserProfile | null) {
  if (profile) {
    const hydrated = hydrateProfileApps(profile);
    localStorage.setItem('archicore_cached_profile', JSON.stringify(hydrated));
  } else {
    localStorage.removeItem('archicore_cached_profile');
  }
}
// -------------------------------------

// --- Mapa de deduplicación en memoria para llamadas concurrentes ---
const inFlightProvisionMap = new Map<string, Promise<UserProfile | null>>();

/**
 * Garantiza que el usuario cuente con documento de organización y perfil en Firestore.
 * Si es usuario nuevo, la Cloud Function creará el tenant y sembrará la configuración.
 */
export async function ensureUserProvisioned(user: User): Promise<UserProfile | null> {
  const uid = user.uid;
  if (inFlightProvisionMap.has(uid)) {
    console.log('[ensureUserProvisioned] Esperando provisión ya en curso para UID:', uid);
    return inFlightProvisionMap.get(uid)!;
  }

  const promise = (async () => {
    try {
      // 1. Verificación preliminar rápida en Firestore: si ya existe, no bloqueamos
      const quickCheckSnap = await getDoc(doc(db, 'users', uid));
      if (quickCheckSnap.exists()) {
        const existingProfile = hydrateProfileApps({ uid, ...quickCheckSnap.data() } as UserProfile);
        cacheUserProfile(existingProfile);
        // Si ya tiene orgId, retornamos directamente para máxima velocidad
        if (existingProfile.orgId) {
          console.log('[ensureUserProvisioned] Perfil existente verificado en Firestore para:', existingProfile.email);
          return existingProfile;
        }
      }

      const idToken = await user.getIdToken();
      const projectId = functions.app.options.projectId || 'control-de-asistencia-en-obra';
      const endpoint = `https://us-central1-${projectId}.cloudfunctions.net/autoProvisionUser`;

      console.log('[ensureUserProvisioned] Solicitando provisión en backend:', endpoint);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        console.warn('[ensureUserProvisioned] Respuesta de autoProvisionUser:', response.status);
      } else {
        const data = await response.json();
        console.log('[ensureUserProvisioned] Provisión confirmada por Cloud Function:', data);
      }

      // Forzar actualización del token para recibir custom claims (orgId, role)
      await user.getIdToken(true);

      // Leer perfil desde Firestore con hasta 3 reintentos rápidos (esperando propagación)
      for (let attempt = 1; attempt <= 3; attempt++) {
        const profileSnap = await getDoc(doc(db, 'users', uid));
        if (profileSnap.exists()) {
          const profile = hydrateProfileApps({ uid, ...profileSnap.data() } as UserProfile);
          cacheUserProfile(profile);
          return profile;
        }
        if (attempt < 3) {
          await new Promise((res) => setTimeout(res, 600));
        }
      }
    } catch (err) {
      console.error('[ensureUserProvisioned] Error asegurando provisión:', err);
    } finally {
      inFlightProvisionMap.delete(uid);
    }
    return null;
  })();

  inFlightProvisionMap.set(uid, promise);
  return promise;
}

/**
 * Inicio de Sesión o Registro Rápido con Google (OAuth 1 Clic)
 * Cuenta con Fast-Path: si el usuario ya existe, entra en ~300ms sin esperar Cloud Functions.
 */
export async function signInWithGoogle(): Promise<AuthResult> {
  try {
    const auth = getAuthInstance();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    
    const userCredential = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
    const user = userCredential.user;

    // Fast-Path: Comprobar inmediatamente en Firestore si ya existe perfil
    const directSnap = await getDoc(doc(db, 'users', user.uid));
    let profile: UserProfile | null = null;

    if (directSnap.exists() && (directSnap.data() as any)?.orgId) {
      profile = hydrateProfileApps({ uid: user.uid, ...directSnap.data() } as UserProfile);
      cacheUserProfile(profile);
      console.log('[signInWithGoogle] ⚡ Fast-Path exitoso para usuario existente:', user.email);
    } else {
      // Nuevo usuario o requiere auto-provisión de despacho
      profile = await ensureUserProvisioned(user);
    }

    localStorage.setItem('archicore_cached_session', JSON.stringify({
      uid: user.uid,
      email: user.email
    }));

    return { 
      success: true, 
      user, 
      profile: profile || undefined,
      isNew: !directSnap.exists()
    };
  } catch (error: any) {
    console.error('[signInWithGoogle] Error:', error);
    return { success: false, code: error?.code, message: error?.message };
  }
}

/**
 * Registro Universal con Cualquier Correo y Contraseña
 */
export async function signUpWithEmail(email: string, password: string, fullName: string): Promise<AuthResult> {
  try {
    const auth = getAuthInstance();
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    if (fullName) {
      await updateProfile(userCredential.user, { displayName: fullName });
    }

    const profile = await ensureUserProvisioned(userCredential.user);

    localStorage.setItem('archicore_cached_session', JSON.stringify({
      uid: userCredential.user.uid,
      email: userCredential.user.email
    }));

    return { success: true, isNew: true, user: userCredential.user, profile: profile || undefined };
  } catch (error: any) {
    console.error('[signUpWithEmail] Error:', error);
    return { success: false, code: error?.code, message: error?.message };
  }
}

/**
 * Inicio de Sesión Tradicional con Correo y Contraseña
 * Incluye Fast-Path para omitir llamadas innecesarias al backend.
 */
export async function signInWithEmail(email: string, password: string): Promise<SignInResult> {
  try {
    const auth = getAuthInstance();
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Fast-Path
    const directSnap = await getDoc(doc(db, 'users', user.uid));
    let profile: UserProfile | null = null;

    if (directSnap.exists() && (directSnap.data() as any)?.orgId) {
      profile = hydrateProfileApps({ uid: user.uid, ...directSnap.data() } as UserProfile);
      cacheUserProfile(profile);
    } else {
      profile = await ensureUserProvisioned(user);
    }

    localStorage.setItem('archicore_cached_session', JSON.stringify({
      uid: user.uid,
      email: user.email
    }));
    return { success: true, user, profile: profile || undefined };
  } catch (error: any) {
    return { success: false, code: error?.code, message: error?.message };
  }
}

export function onAuthChanged(cb: (user: User | null) => void) {
  const auth = getAuthInstance();
  return firebaseOnAuthStateChanged(auth, cb);
}

export async function signOut(): Promise<void> {
  const auth = getAuthInstance();
  localStorage.removeItem('archicore_cached_session');
  localStorage.removeItem('archicore_cached_profile');
  await firebaseSignOut(auth);
}

export const getOrganization = async (orgId: string): Promise<Organization | null> => {
  const orgDoc = await getDoc(doc(db, 'organizations', orgId));
  if (orgDoc.exists()) {
    return { id: orgId, ...orgDoc.data() } as Organization;
  }
  return null;
};

export const subscribeToOrganization = (orgId: string, callback: (org: Organization) => void) => {
  return onSnapshot(doc(db, 'organizations', orgId), (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: orgId, ...docSnap.data() } as Organization);
    }
  });
};

